import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { SectionCard } from "@/components/common/SectionCard";
import Button from "@/components/common/Button";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import Pagination from "@/components/common/Pagination";
import {
    Lock,
    Search,
    RefreshCw,
    ShoppingBag,
    TrendingDown,
    Wallet,
    Receipt,
    CheckCircle2,
    Hash,
} from "lucide-react";
import { formatRand, GetFilterUrl, showToastNotification, formatDateTime } from "@/utility/helper";
import { callApi, ThrowError } from "@/api/callApi";
import { API_URL, API_ENDPOINT } from "@/api/constant";
import { IStore } from '@/interfaces/Store/store.interface';
import { MultiSelect } from "@/components/common/MultiSelect";
import { useNavigate } from 'react-router-dom';
import { ISummary } from '@/interfaces/CashSettlement/cashSettlement.interface';
import {
    IStoreSettlementResponse,
    IStoreEarningListResponse,
    IEarningList,
} from '@/interfaces/IStoreSettlement/IStoreSettlement.interface';
import FormField from "@/components/common/FormField";
import { IOrder } from '@/interfaces/Order/order.interface';
import { IPageInfo } from '@/interfaces/common.interface';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';

// ── Summary Metric Card ──────────────────────────────────────────
interface MetricCardProps {
    label: string;
    value: string | number;
    icon: React.ReactNode;
    iconBg: string;
    valueColor?: string;
    sublabel?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
    label, value, icon, iconBg, valueColor = 'text-foreground', sublabel,
}) => (
    <div className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4 shadow-sm">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
            {icon}
        </div>
        <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{label}</p>
            <p className={`text-xl font-bold mt-0.5 ${valueColor}`}>{value}</p>
            {sublabel && <p className="text-xs text-muted-foreground mt-0.5">{sublabel}</p>}
        </div>
    </div>
);

// ── Default page info ─────────────────────────────────────────────
const DEFAULT_PAGE_INFO: IPageInfo = { limit: 10, page: 1, total: 0, totalPages: 0 };

export const CreateStoreSettlement = () => {
    const roleNavigate = useRoleNavigate();
    const navigate = useNavigate();
    const [stores, setStores] = useState<IStore[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    // Summary state (from /store/earning)
    const [fetchingSummary, setFetchingSummary] = useState(false);
    const [summary, setSummary] = useState<ISummary | null>(null);
    const [lockingPayout, setLockingPayout] = useState(false);

    // Order list state (from /store/earning-list)
    const [orders, setOrders] = useState<IEarningList[]>([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [orderPage, setOrderPage] = useState(1);
    const [orderPageInfo, setOrderPageInfo] = useState<IPageInfo>(DEFAULT_PAGE_INFO);
    const ORDER_PAGE_SIZE = 10;

    // Whether filters have been applied (controls showing results section)
    const [applied, setApplied] = useState(false);

    // ── Stores ───────────────────────────────────────────────────
    const fetchStores = async (search: string = "") => {
        try {
            const filterParems = GetFilterUrl({
                search,
                page: 1,
                limit: 10,
            })
            const res = await callApi<{ data: { stores: IStore[] } }>(
                `${API_URL}${API_ENDPOINT.stores}/all?${filterParems}`,
                { method: 'GET' }
            );
            setStores(res?.data?.stores || []);
        } catch (error) {
            ThrowError(error);
        }
    };

    useEffect(() => { fetchStores(); }, []);

    // ── Fetch paginated order list ────────────────────────────────
    const fetchOrderList = useCallback(async (page: number) => {
        if (!selectedStoreId || !startDate || !endDate) return;
        try {
            setLoadingOrders(true);
            const queryParams = GetFilterUrl({
                storeId: selectedStoreId,
                startDate,
                endDate,
                page,
                limit: ORDER_PAGE_SIZE,
            });
            const res = await callApi<IStoreEarningListResponse>(
                `${API_URL}${API_ENDPOINT.storeEarningList}?${queryParams}`,
                { method: 'GET' }
            );
            setOrders(res?.data?.earnings || []);
            setOrderPageInfo(res?.data?.pageInfo ?? DEFAULT_PAGE_INFO);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingOrders(false);
        }
    }, [selectedStoreId, startDate, endDate]);

    // Re-fetch order list when page changes (only after summary is loaded)
    useEffect(() => {
        if (applied) {
            fetchOrderList(orderPage);
        }
    }, [orderPage, applied]);

    // ── Get Summary (calls both APIs in parallel) ─────────────────
    const handleFetchSummary = async () => {
        if (!selectedStoreId || !startDate || !endDate) {
            showToastNotification('danger', 'Please select a store, start date, and end date.');
            return;
        }
        try {
            setFetchingSummary(true);
            setSummary(null);
            setOrders([]);
            setOrderPage(1);
            setApplied(false);

            const baseParams = GetFilterUrl({ storeId: selectedStoreId, startDate, endDate });
            const listParams = GetFilterUrl({
                storeId: selectedStoreId,
                startDate,
                endDate,
                page: 1,
                limit: ORDER_PAGE_SIZE,
            });

            const [summaryRes, listRes] = await Promise.all([
                callApi<IStoreSettlementResponse>(
                    `${API_URL}${API_ENDPOINT.stores}/earning?${baseParams}`,
                    { method: 'GET' }
                ),
                callApi<IStoreEarningListResponse>(
                    `${API_URL}${API_ENDPOINT.storeEarningList}?${listParams}`,
                    { method: 'GET' }
                ),
            ]);

            setSummary(summaryRes?.data);
            setOrders(listRes?.data?.earnings || []);
            setOrderPageInfo(listRes?.data?.pageInfo ?? DEFAULT_PAGE_INFO);
            setApplied(true);
        } catch (error) {
            ThrowError(error);
        } finally {
            setFetchingSummary(false);
        }
    };

    // ── Pagination handler ────────────────────────────────────────
    const handleOrderPageChange = (page: number) => {
        setOrderPage(page);
        // useEffect will call fetchOrderList
    };

    // ── Lock Payout ───────────────────────────────────────────────
    const handleLockPayout = async () => {
        if (!summary || !selectedStoreId) return;
        try {
            setLockingPayout(true);
            const payload = {
                storeId: selectedStoreId,
                amount: summary.totalEarnings.toFixed(2),
                startDate,
                endDate,
                totalOrders: summary.totalOrders ?? 0,
            };
            await callApi(
                `${API_URL}${API_ENDPOINT.payouts}/lock`,
                { method: 'POST', data: payload }
            );
            showToastNotification('success', 'Payout locked successfully!');
            navigate(-1);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLockingPayout(false);
        }
    };

    // ── Derived values (direct from API) ─────────────────────────
    const totalCollectedAmount = summary?.totalCollectedAmount ?? 0;
    const totalCommissionAmount = summary?.totalCommissionAmount ?? 0;
    const totalEarnings = summary?.totalEarnings ?? 0;

    // ── Order history table columns ───────────────────────────────
    const columns: TableColumn<IEarningList>[] = [
        {
            key: 'orderId',
            header: 'Order ID',
            render: (item) => {
                const order = item.order || item;
                return (
                    <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => roleNavigate(RoutesLink.ViewOrders.replace(':id', order.id))}
                    >
                        <Hash className="w-3 h-3 text-muted-foreground" />
                        <span className="font-mono text-sm font-semibold text-primary underline underline-offset-2">{order.orderId}</span>
                    </div>
                )
            }
        },
        {
            key: 'createdAt',
            header: 'Date',
            render: (item) => (
                <span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span>
            ),
        },
        {
            key: 'totalAmount',
            header: 'Order Amount',
            render: (item) => (
                <span className="font-semibold text-foreground text-sm">
                    {formatRand(parseFloat(item.commissionableAmount || '0'))}
                </span>
            ),
        },
        {
            key: 'commissionAmount',
            header: 'Commission',
            render: (item) => (
                <div className="flex items-center gap-1">
                    <span className="text-sm font-semibold text-destructive">
                        -{formatRand(parseFloat(item?.commissionAmount || '0'))}
                    </span>
                    {item?.commissionPercentage && (
                        <span className="text-xs text-muted-foreground">
                            ({item?.commissionPercentage}%)
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'storePayableAmount',
            header: 'Store Payable',
            render: (item) => (
                <span className="text-sm font-bold text-green-600">
                    {formatRand(parseFloat(item?.storePayableAmount || '0'))}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => <StatusBadge status={item?.isSettled ? "completed" : "pending"} />,
        },
    ];

    const storeOptions = stores.map(s => ({ label: s.name, value: s.id }));
    const today = new Date().toISOString().split('T')[0];

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Generate Store Payout"
                    description="Select a store and date range to preview earnings, then lock the payout."
                    showBack
                    onBack={() => navigate(-1)}
                />

                {/* ── Filter & Generate ──────────────────────────── */}
                <SectionCard title="Filter & Generate" icon={<Search className="w-5 h-5 text-primary" />}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-foreground mb-1.5">Select Store</label>
                            <MultiSelect
                                options={storeOptions}
                                selected={selectedStoreId ? [selectedStoreId] : []}
                                onSearchChange={(search) => fetchStores(search)}
                                onChange={(val) => setSelectedStoreId(val[0] || "")}
                                placeholder="-- Select Store --"
                                singleSelect={true}
                            />
                        </div>
                        <FormField
                            wrapperClass=""
                            label="Start Date"
                            name="startDate"
                            type="date"
                            value={startDate}
                            max={today}
                            onChange={(e: any) => {
                                setStartDate(e.target.value);
                                if (endDate && e.target.value > endDate) setEndDate('');
                            }}
                        />
                        <FormField
                            wrapperClass=""
                            label="End Date"
                            name="endDate"
                            type="date"
                            value={endDate}
                            min={startDate}
                            max={today}
                            onChange={(e: any) => setEndDate(e.target.value)}
                        />
                        <Button
                            onClick={handleFetchSummary}
                            disabled={!selectedStoreId || !startDate || !endDate}
                            isLoading={fetchingSummary}
                            variant="primary"
                            leftIcon={<RefreshCw size={16} />}
                            className="h-10 w-full"
                        >
                            Get Summary
                        </Button>
                    </div>
                </SectionCard>

                {/* ── Results (shown after Get Summary) ─────────── */}
                {summary && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">

                        {/* 4-metric summary row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                            <MetricCard
                                label="Total Orders"
                                value={summary.totalOrders}
                                icon={<ShoppingBag className="w-5 h-5 text-blue-600" />}
                                iconBg="bg-blue-100"
                                sublabel="Orders in selected period"
                            />
                            <MetricCard
                                label="Total Order Amount"
                                value={formatRand(totalCollectedAmount)}
                                icon={<Receipt className="w-5 h-5 text-violet-600" />}
                                iconBg="bg-violet-100"
                                sublabel="Gross revenue from orders"
                            />
                            <MetricCard
                                label="Commission Deducted"
                                value={formatRand(totalCommissionAmount)}
                                icon={<TrendingDown className="w-5 h-5 text-rose-600" />}
                                iconBg="bg-rose-100"
                                valueColor="text-rose-600"
                                sublabel="Platform fee charged"
                            />
                            <MetricCard
                                label="Final Payable Amount"
                                value={formatRand(totalEarnings)}
                                icon={<Wallet className="w-5 h-5 text-green-600" />}
                                iconBg="bg-green-100"
                                valueColor="text-green-600"
                                sublabel="Amount to be paid to store"
                            />
                        </div>

                        {/* Order history table */}
                        <SectionCard
                            title={`Order History (${orderPageInfo.total} order${orderPageInfo.total !== 1 ? 's' : ''})`}
                            icon={<ShoppingBag className="w-5 h-5 text-primary" />}
                        >
                            <CommonTable
                                columns={columns}
                                data={orders}
                                loading={loadingOrders}
                                emptyMessage="No orders found for the selected period."
                            />
                            <Pagination
                                currentPage={orderPage}
                                totalPages={orderPageInfo.totalPages}
                                totalItems={orderPageInfo.total}
                                itemsPerPage={ORDER_PAGE_SIZE}
                                onPageChange={handleOrderPageChange}
                            />
                        </SectionCard>

                        {/* Lock Payout action bar */}
                        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h4 className="text-base font-bold text-foreground">Ready to Lock Payout?</h4>
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                        This will create a payout record of{' '}
                                        <span className="font-semibold text-green-600">{formatRand(totalEarnings)}</span>{' '}
                                        for{' '}
                                        <span className="font-medium text-foreground">{summary.totalOrders} orders</span>{' '}
                                        and mark them as settled.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={handleLockPayout}
                                isLoading={lockingPayout}
                                disabled={summary.totalEarnings <= 0}
                                loadingText="Locking..."
                                leftIcon={<Lock className="w-4 h-4" />}
                                className="shrink-0 rounded-xl shadow-lg shadow-primary/20 gap-2 h-12 px-8 text-base"
                            >
                                Lock Payout
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};
